import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import {
    Checkbox,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
} from '@chakra-ui/react'

import { MatchEventPeriodNumber, MinuteRange, timeRangePerEventPeriod } from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'
import { useSelectedEventContext } from '@contexts/SelectedEventContext'

export function TimeInputGroup() {
    const { selectedEvent } = useSelectedEventContext()
    const { zodForm, isAdditionalTimeSelected, setIsAdditionalTimeSelected, isFormDisabled } =
        useMatchEditorFormContext()

    const [minuteRange, setMinuteRange] = useState<MinuteRange>(
        () => timeRangePerEventPeriod[MatchEventPeriodNumber.first_half],
    )

    const selectedPeriod = Number(zodForm.watch('period_number').value)

    const hasMatchTimeError =
        !!zodForm.formState.errors.time?.root?.message ||
        !!zodForm.formState.errors.time?.minutes?.message ||
        !!zodForm.formState.errors.time?.seconds?.message ||
        (isAdditionalTimeSelected && !!zodForm.formState.errors.time?.additionalMinutes?.message)

    const handleAdditionalTimeToggleByUser = () => {
        const newAdditionalTimeStatus = !isAdditionalTimeSelected

        setIsAdditionalTimeSelected(newAdditionalTimeStatus)
    }

    useEffect(() => {
        if (!selectedPeriod) {
            return
        }

        const selectedPeriodRange: MinuteRange = { ...timeRangePerEventPeriod[selectedPeriod] }

        const timeParts = selectedEvent?.event_time?.split('+') ?? [
            `${selectedPeriodRange.min ? selectedPeriodRange.min : '00'}:00`,
        ]

        const [matchTime, additionalTime] = timeParts
        const [matchMinutes, matchSeconds] = matchTime.split(':').map(Number)

        let additionalMinutes = 0
        let additionalSeconds = 0

        if (additionalTime) {
            const [extraMinutes, extraSeconds] = additionalTime.split(':').map(Number)
            additionalMinutes = extraMinutes

            additionalSeconds = extraSeconds
        }

        if (isAdditionalTimeSelected) {
            selectedPeriodRange.min = selectedPeriodRange.max
        }

        setMinuteRange(selectedPeriodRange)

        zodForm.setValue(
            'time',
            {
                minutes: isAdditionalTimeSelected ? selectedPeriodRange.max : matchMinutes,
                seconds: isAdditionalTimeSelected ? additionalSeconds : matchSeconds,
                additionalMinutes: String(additionalMinutes),
            },
            { shouldDirty: false },
        )
    }, [isAdditionalTimeSelected, selectedEvent?.event_time, selectedPeriod, zodForm])

    return (
        <FormControl isInvalid={hasMatchTimeError} isRequired isDisabled={isFormDisabled} mb={8}>
            <HStack mb={2}>
                <FormLabel htmlFor="time.minutes" mb={0}>
                    Time
                </FormLabel>
                <Checkbox
                    isRequired={false}
                    formNoValidate
                    defaultChecked={isAdditionalTimeSelected}
                    isChecked={isAdditionalTimeSelected}
                    isDisabled={isFormDisabled}
                    onChange={handleAdditionalTimeToggleByUser}>
                    Additional time
                </Checkbox>
            </HStack>
            <HStack>
                <Controller
                    control={zodForm.control}
                    name="time.minutes"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <FormControl>
                            <NumberInput
                                max={minuteRange.max}
                                min={minuteRange.min}
                                defaultValue={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                isDisabled={isFormDisabled}
                                value={value}>
                                <NumberInputField ref={ref} name={name} />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                            <FormHelperText>Minutes</FormHelperText>
                        </FormControl>
                    )}
                />
                {isAdditionalTimeSelected && (
                    <Controller
                        control={zodForm.control}
                        name="time.additionalMinutes"
                        render={({ field: { onChange, onBlur, value, name, ref } }) => (
                            <FormControl>
                                <NumberInput
                                    max={30}
                                    min={0}
                                    defaultValue={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    isDisabled={isFormDisabled}
                                    value={value}>
                                    <NumberInputField ref={ref} name={name} />
                                    <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                    </NumberInputStepper>
                                </NumberInput>
                                <FormHelperText>Minutes (additional)</FormHelperText>
                            </FormControl>
                        )}
                    />
                )}
                <Controller
                    control={zodForm.control}
                    name="time.seconds"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <FormControl>
                            <NumberInput
                                max={59}
                                min={0}
                                defaultValue={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value}
                                isDisabled={isFormDisabled}
                                isRequired>
                                <NumberInputField ref={ref} name={name} />
                                <NumberInputStepper>
                                    <NumberIncrementStepper />
                                    <NumberDecrementStepper />
                                </NumberInputStepper>
                            </NumberInput>
                            <FormHelperText>Seconds</FormHelperText>
                        </FormControl>
                    )}
                />
            </HStack>
            <FormErrorMessage>{zodForm.formState.errors.time?.root?.message}</FormErrorMessage>
            <FormErrorMessage>{zodForm.formState.errors.time?.minutes?.message}</FormErrorMessage>
            <FormErrorMessage>{zodForm.formState.errors.time?.additionalMinutes?.message}</FormErrorMessage>
            <FormErrorMessage>{zodForm.formState.errors.time?.seconds?.message}</FormErrorMessage>
        </FormControl>
    )
}
